import type { LeadKeyFact } from "@shared/lib/leads-shared";
import { StatusDot } from "@shared/components/blocks";
import { cn } from "@shared/lib/utils";
import { SourceAnchor } from "@/components/source-anchor";
import { useT } from "@shared/client/locale";

const STATUS_TONES: Record<LeadKeyFact["status"], string> = {
  confirmed: "success",
  inferred: "warning",
  unknown: "neutral",
};

/**
 * Les critères de qualification de l'offre, tels que l'enquête y a répondu :
 * une valeur courte en gros, lisible d'un coup d'œil, puis sa preuve — ou,
 * faute de preuve, la question à poser. Les critères décisifs d'abord.
 */
export function LeadKeyFacts({ facts }: { facts: LeadKeyFact[] }) {
  const copy = useT().leads.keyFacts;
  if (facts.length === 0) return null;
  const ordered = [...facts.filter((fact) => fact.decisive), ...facts.filter((fact) => !fact.decisive)];
  const established = facts.filter((fact) => fact.status !== "unknown").length;

  return (
    <section className="grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="m-0 text-[14px] font-semibold">{copy.title}</h3>
        <span className="text-[12px] text-muted-foreground">
          {copy.established(established, facts.length)}
        </span>
      </div>
      <dl className="m-0 grid gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((fact) => (
          <div className="grid content-start gap-1 bg-background px-4 py-3.5" key={fact.criterion}>
            <dt className="text-[12px] text-muted-foreground">
              {fact.criterion}
              {fact.decisive && <span className="text-foreground"> · {copy.decisive}</span>}
            </dt>
            <dd className="m-0 grid gap-1">
              <span
                className={cn(
                  "text-[19px] leading-tight font-medium tracking-[-0.01em] tabular-nums",
                  fact.status === "unknown" && "font-normal text-muted-foreground",
                )}
              >
                {fact.value ?? copy.notFound}
              </span>
              {fact.detail && <span className="text-[12.5px] leading-snug text-muted-foreground">{fact.detail}</span>}
              {fact.status !== "unknown" && (
                <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <StatusDot tone={STATUS_TONES[fact.status]}>{copy.statuses[fact.status]}</StatusDot>
                  {fact.source_url && <SourceAnchor className="text-[12px]" href={fact.source_url} iconSize={14} />}
                </span>
              )}
              {fact.question && (
                <span className="mt-1 text-[12.5px] leading-snug">
                  <strong className="font-semibold">{copy.ask}</strong>
                  {fact.question}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
